import { useState } from "preact/hooks";
import {
  RANGES,
  csToCm360,
  degreesPerCount,
  settings,
  updateSettings,
} from "../../../settings";
import type { Settings } from "../../../settings";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { Slider } from "../../components/Slider";
import { Toggle } from "../../components/Toggle";
import {
  SensitivityConverter,
  formatCm360,
  sensDpi,
} from "./SensitivityConverter";

export function ControlsTab() {
  const c = settings.value.controls;
  const dpi = sensDpi.value;
  const [converting, setConverting] = useState(false);
  const set = (patch: Partial<Settings["controls"]>) =>
    updateSettings({ controls: patch });

  return (
    <div class="settings__content">
      <h3 class="settings__group">Mouse</h3>
      <div class="settings__rows">
        <Slider
          label="Sensitivity"
          description="CS-equivalent: degrees per count = value x 0.022."
          decimals={3}
          {...RANGES.sensitivity}
          value={c.sensitivity}
          onChange={(sensitivity) => set({ sensitivity })}
        />
        <div class="settings__summary">
          <p class="settings__summary-text num" aria-live="polite">
            <span class="settings__lcd">
              {degreesPerCount(c.sensitivity).toFixed(5)}
            </span>
            °/count,{" "}
            <span class="settings__lcd">
              {formatCm360(csToCm360(c.sensitivity, dpi))}
            </span>{" "}
            cm per 360° turn at <span class="settings__lcd">{dpi}</span> DPI
          </p>
          <Button size="sm" onClick={() => setConverting(true)}>
            Convert
          </Button>
        </div>
        <Slider
          label="ADS sensitivity"
          description="Multiplier while aiming down sights. 1 keeps the hip-fire screen speed at every zoom."
          unit="x"
          {...RANGES.adsSensitivityMult}
          value={c.adsSensitivityMult}
          onChange={(adsSensitivityMult) => set({ adsSensitivityMult })}
        />
        <Slider
          label="Scope sensitivity"
          description="Multiplier while looking through a scope. 1 keeps the hip-fire screen speed at every zoom."
          unit="x"
          {...RANGES.scopeSensitivityMult}
          value={c.scopeSensitivityMult}
          onChange={(scopeSensitivityMult) => set({ scopeSensitivityMult })}
        />
        <Toggle
          label="Invert Y axis"
          description="Moving the mouse up looks down."
          checked={c.invertY}
          onChange={(invertY) => set({ invertY })}
        />
        <Toggle
          label="Raw mouse input"
          description="Uses unadjusted mouse movement where the browser supports it."
          checked={c.rawInput}
          onChange={(rawInput) => set({ rawInput })}
        />
      </div>

      <h3 class="settings__group">Actions</h3>
      <div class="settings__rows">
        <Toggle
          label="Toggle aim down sights"
          description="Press once to aim, again to stop, instead of holding."
          checked={c.toggleAds}
          onChange={(toggleAds) => set({ toggleAds })}
        />
        <Toggle
          label="Toggle crouch"
          description="Press once to crouch, again to stand, instead of holding."
          checked={c.toggleCrouch}
          onChange={(toggleCrouch) => set({ toggleCrouch })}
        />
        <Toggle
          label="Jump on mouse wheel"
          description="Scrolling up or down presses Jump. The wheel no longer switches weapons."
          checked={c.jumpOnWheel}
          onChange={(jumpOnWheel) => set({ jumpOnWheel })}
        />
      </div>

      {converting && (
        <Modal
          title="Convert sensitivity"
          size="md"
          onClose={() => setConverting(false)}
        >
          <SensitivityConverter onClose={() => setConverting(false)} />
        </Modal>
      )}
    </div>
  );
}
